import { Alpine, collapse, defineXComponent, focus, intersect } from "@various/alpine";
import { exportGlobal, formFetch, formFetchHtml, formFetchJson } from "@various/common";

import lazySizes from "lazysizes";
import "lazysizes/plugins/unveilhooks/ls.unveilhooks";

import { accordionDataContext } from "./lib/accordion";
import { confetti, defaultConfettiFire } from "./lib/confetti";
import { extendExtbaseDebugger } from "./lib/debug";
import { programDataContext } from "./lib/program";
import { SwiperXComponent } from "./lib/swiper";
import { initTokenFragmentRedirect } from "./lib/token-fragment";
import { jwPlayerDataContext } from "./lib/player";
import { loadItemsDataContext } from "./lib/load-items";
import { legacyDropDownDataContext } from "./lib/legacy";
import { naturalSearchDataContext } from "./lib/natural-search";
import { videoFilterBarDataContext } from "./lib/video-filter-bar";
import { searchFeedbackDataContext } from "./lib/search-feedback";
import { paymentFormDataContext } from "./lib/payment";
import { pushDataLayerEvent } from "./lib/analytics";
import { boxesParallaxDataContext } from "./lib/boxes-parallax";
import {
  initializeFormValidation,
  registerFormValidator,
  validateForm,
  validateFormAsync,
} from "./lib/form-validation";
import { formDataContext } from "./lib/form";
import "./lib/recaptcha";
import "./lib/voucher-registration";
import { ymhEntrytestV2 } from "./entrytest-v2/entrytest-v2";
import "./entrytest-v2/entrytest-v2.scss";

import "./styles/default.scss";

Alpine.plugin(collapse);
Alpine.plugin(intersect);
// x-trap for accessible modals (video search feedback)
Alpine.plugin(focus);

initTokenFragmentRedirect();
extendExtbaseDebugger();

defineXComponent("swiper", SwiperXComponent);
Alpine.data("vrsAccordion", accordionDataContext);
Alpine.data("ymhProgram", programDataContext);
Alpine.data("ymhJwPlayer", jwPlayerDataContext);
Alpine.data("loadItems", loadItemsDataContext);
Alpine.data("legacyDropDown", legacyDropDownDataContext);
Alpine.data("ymhNaturalSearch", naturalSearchDataContext);
Alpine.data("ymhVideoFilterBar", videoFilterBarDataContext);
Alpine.data("ymhSearchFeedback", searchFeedbackDataContext);
Alpine.data("paymentForm", paymentFormDataContext);
Alpine.data("ymhBoxesParallax", boxesParallaxDataContext);
Alpine.data("ymhForm", formDataContext);
// Existing templates can migrate progressively; both names use the same
// generic form controller and no CAPTCHA-specific Alpine context remains.
Alpine.data("ymhFormValidation", formDataContext);
Alpine.data("ymhEntrytestV2", ymhEntrytestV2);

function containsEmojiCharactersOnly(value: string): boolean {
  try {
    return !String(value).match(/[^\s\uFE00-\uFE0F\p{Extended_Pictographic}\u200B-\u200D\u2060\uFEFF]/u);
  } catch {
    return false;
  }
}

function handleCrispChatVisibility() {
  try {
    $crisp && $crisp.push(["do", window.matchMedia("(max-width: 780px)").matches
      && screen.orientation.type.startsWith("landscape") ? "chat:hide" : "chat:show"]);
  } catch { /* ignore */ }
}
// iOS < 16.4 kennt screen.orientation nicht — ohne Guard stirbt hier die
// gesamte Bundle-Initialisierung (Top-Level!) auf diesen Geräten.
screen.orientation?.addEventListener("change", () => handleCrispChatVisibility());
window.CRISP_READY_TRIGGER = function() { handleCrispChatVisibility(); };

exportGlobal({ confetti, defaultConfettiFire });
exportGlobal({ formFetch, formFetchHtml, formFetchJson });
exportGlobal({ containsEmojiCharactersOnly });
exportGlobal({ debounce: Alpine.debounce });
exportGlobal({
  ymhInitializeFormValidation: initializeFormValidation,
  ymhRegisterFormValidator: registerFormValidator,
  ymhValidateForm: validateForm,
  ymhValidateFormAsync: validateFormAsync,
  ymhPushDataLayerEvent: pushDataLayerEvent,
});

window["TW_BREAKPOINTS"] = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  x2: 1536,
  x3: 1920,
  x4: 2304,
};

lazySizes.cfg.init = false;
lazySizes.init();

Alpine.start();

// Enable endless scroll (autoload) for all users on the video listing page.
// The Fluid template only sets "autoload" for logged-in users (feUser).
// We upgrade all load-more buttons inside the video list after page load
// and after each AJAX append (via MutationObserver).
(() => {
  function upgradeLoadMoreButtons() {
    document
      .querySelectorAll(".tx-va-yoga-video-list-items .load-more-items:not(.autoload)")
      .forEach((btn) => btn.classList.add("autoload"));
    (window as any).varioous?.ajax_cl?.initLoadMoreItemsButtons?.();
  }

  // Initial upgrade after DOM + scripts are ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", upgradeLoadMoreButtons);
  } else {
    // DOMContentLoaded already fired (script loaded with defer or at end of body)
    setTimeout(upgradeLoadMoreButtons, 100);
  }

  // Watch for new load-more buttons appended by ajax.js after each page load
  const listContainer = document.querySelector(".tx-va-yoga-video-list-items");
  if (listContainer) {
    new MutationObserver(() => setTimeout(upgradeLoadMoreButtons, 50))
      .observe(listContainer, { childList: true, subtree: true });
  }
})();
